import { Fragment, useMemo, useState } from 'react';

function valueFor(row, column) {
  const value = typeof column.accessor === 'function' ? column.accessor(row) : row[column.accessor];
  if (value?.toDate) return value.toDate().getTime();
  return value ?? '';
}

export default function SortableTable({
  columns,
  rows,
  filter = '',
  emptyText = 'No rows found.',
  rowKey = (row) => row.id,
  className = '',
  defaultSort,
  renderExpanded,
}) {
  const [sort, setSort] = useState(defaultSort || { key: columns[0]?.key || '', dir: 'asc' });
  const needle = filter.trim().toLowerCase();

  const visibleRows = useMemo(() => {
    const filtered = needle
      ? rows.filter((row) => columns.some((column) => {
        const value = valueFor(row, column);
        return String(value).toLowerCase().includes(needle);
      }))
      : rows;

    const active = columns.find((column) => column.key === sort.key && column.sortable !== false);
    if (!active) return filtered;

    return [...filtered].sort((a, b) => {
      const left = valueFor(a, active);
      const right = valueFor(b, active);
      const result = String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
      return sort.dir === 'asc' ? result : -result;
    });
  }, [columns, needle, rows, sort]);

  const setColumnSort = (key) => {
    setSort((current) => ({
      key,
      dir: current.key === key && current.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <div className={`wb-table-shell ${className}`}>
      <table className="wb-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key}>
                {column.sortable === false ? (
                  <span className="wb-th-static">{column.header}</span>
                ) : (
                  <button
                    type="button"
                    className="wb-th-sort"
                    onClick={() => setColumnSort(column.key)}
                  >
                    <span>{column.header}</span>
                    <span aria-hidden="true">{sort.key === column.key ? (sort.dir === 'asc' ? 'ASC' : 'DESC') : '--'}</span>
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((row) => (
            <Fragment key={rowKey(row)}>
              <tr>
                {columns.map((column) => (
                  <td key={column.key}>
                    {column.render ? column.render(row) : String(valueFor(row, column) || '-')}
                  </td>
                ))}
              </tr>
              {renderExpanded ? renderExpanded(row, columns.length) : null}
            </Fragment>
          ))}
          {visibleRows.length === 0 ? (
            <tr>
              <td colSpan={columns.length}>
                <div className="wb-empty-row">{emptyText}</div>
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
